'use client';

import { CaretUp, InfoIcon, PlusTrans } from 'assets/icons';
import {
  RING_BUY_GTM_EVENTS,
  trackRingBuyOption,
} from 'components/ring-air/analytics/ringBuyEvents';
import { ReactNode, useContext, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { useTranslations } from 'next-intl';
import { CustomImage as Image } from '..';
import { OffersModalNewContext } from 'components/shared/OffersModal';
import styles from './OffersCarousel.module.css';

export type OffersDataType = {
  image?: string[];
  imageType?: 'circle' | 'square';
  title: ReactNode;
  displayValue?: {
    icons: string[];
    text: ReactNode;
  };
  onClick?: () => void;
  modal?: ReactNode;
  // Optional: overrides default content height (56px). Accepts number (px) or string (e.g., '64px')
  contentHeight?: number | string;
};

export const OffersCarousel = ({
  className,
  data,
  elementBgColor,
  leftAligned = false,
  $isRarePage = false,
}: {
  className?: string;
  data: OffersDataType[];
  elementBgColor?: string;
  leftAligned?: boolean;
  $isRarePage?: boolean;
}) => {
  const t = useTranslations('common');
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: leftAligned ? 'start' : 'center',
      slidesToScroll: 1,
      skipSnaps: false,
      loop: true,
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })],
  );

  // const RenderOfferElement = (props: OffersDataType) => {
  // }

  const OffersModalNewClient = useContext(OffersModalNewContext);

  const handleImageModal = ({ modal }: { modal?: ReactNode }) => {
    // va.track("sleep contributor modal - CLICK");
    OffersModalNewClient.set(modal);
    OffersModalNewClient.show();
  };

  const elementColor = elementBgColor || '#f8f8f8'; // Default background color for offer elements

  // Calculate the maximum height from all offers to ensure consistent card heights
  // const maxContentHeight = useMemo(() => {
  //   const defaultHeight = 56; // Default height in pixels
  //   const heights = data.map((offer) => {
  //     if (typeof offer.contentHeight === 'number') return offer.contentHeight;
  //     if (typeof offer.contentHeight === 'string') {
  //       const parsed = parseInt(offer.contentHeight.replace('px', ''));
  //       return isNaN(parsed) ? defaultHeight : parsed;
  //     }
  //     return defaultHeight;
  //   });
  //   return Math.max(...heights) + 'px';
  // }, [data]);

  const renderOfferElement = (
    offer: OffersDataType,
    elementColor: string,
    index?: number,
  ) => {
    return (
      <div
        className={styles.offerElement}
        // contentHeight={maxContentHeight}
        key={index}
        onClick={
          offer.onClick || offer.modal
            ? () => {
                trackRingBuyOption(RING_BUY_GTM_EVENTS.HEROES_OFFER, {
                  action: 'open',
                  title: String(offer.title ?? '').slice(0, 80),
                });
                if (offer.onClick) offer.onClick();
                else handleImageModal({ modal: offer.modal });
              }
            : undefined
        }
        style={
          {
            ['--element-color']: elementColor,
            cursor: offer.onClick ? 'pointer' : undefined,
          } as React.CSSProperties
        }
      >
        <div className={styles.content}>
          {offer.image && offer.image.length > 0 ? (
            <div
              className={`${styles.imageContainer} ${
                offer.imageType === 'square' ? styles.square : ''
              }`}
            >
              {offer.image.map((img, index) => (
                <Image src={img} alt="" height="24" width="24" key={index} />
              ))}
            </div>
          ) : null}
          <p>{offer.title}</p>
          {offer.onClick ? (
            <InfoIcon
              className={styles.actionIcon}
              fill="rgba(0,0,0,0.3)"
              style={{ cursor: 'pointer' }}
              aria-hidden="true"
            />
          ) : null}
          {offer.modal ? (
            <PlusTrans
              className={`${styles.actionIcon} ${styles.plus}`}
              style={{ cursor: 'pointer' }}
              aria-hidden="true"
            />
          ) : null}
        </div>
      </div>
    );
  };

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (emblaApi) {
      const autoplay = emblaApi.plugins().autoplay as any;
      if (autoplay) {
        const onPointerDown = () => autoplay.stop();
        const onPointerUp = () => autoplay.play();

        emblaApi.on('pointerDown', onPointerDown);
        emblaApi.on('pointerUp', onPointerUp);

        return () => {
          emblaApi.off('pointerDown', onPointerDown);
          emblaApi.off('pointerUp', onPointerUp);
        };
      }
    }
  }, [emblaApi]);

  if (data.length < 1) return null;

  if (data.length === 1) return renderOfferElement(data[0], elementColor, 0);

  return (
    <div
      className={className ? `${styles.root} ${className}` : styles.root}
      data-left-aligned={leftAligned ? 'true' : undefined}
      data-rare={$isRarePage ? 'true' : undefined}
    >
      <div className={styles.embla} ref={emblaRef}>
        <div className={styles.emblaContainer}>
          {data.map((offer, index) => (
            <div className={styles.emblaSlide} key={index}>
              {renderOfferElement(offer, elementColor, index)}
            </div>
          ))}
        </div>
      </div>
      <button
        className={styles.nextBtn}
        onClick={scrollNext}
        aria-label={t('offersCarousel.button.ariaLabel.next')}
        title={t('offersCarousel.button.title.next')}
      >
        <CaretUp
          fill="#ffffff"
          style={{
            transform: 'rotate(90deg)',
            width: '12px',
            height: 'auto',
          }}
        />
      </button>
    </div>
  );
};
